import { formatAddress, toHumanReadable } from '@core/logic';
import {
  useGetContainableSplineElementsQuery,
  useGetSplineElementContainedAttributesQuery,
} from '@core/redux/query';
import { pointElementSelectors } from '@elements/redux/point-element.slice';
import {
  pointElementSubtypeAbbreviations,
  type PointElement,
  type PointElementSubtype,
  type SplineElement,
} from '@elements/types';
import { Autocomplete, Grid2 as Grid, lighten, ListItem, TextField } from '@mui/material';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

export interface ElementDialogProps<T = SplineElement | PointElement> {
  elementToUpdate?: T;
  onClose: () => void;
  onDelete?: () => void;
}

export const DIALOG_WIDTH = 600;

export const ParentSelectAutocomplete = ({
  elementToUpdate,
  updateProp,
}: {
  elementToUpdate: ElementDialogProps['elementToUpdate'];
  updateProp: (key: string, value: string) => void;
}) => {
  const allNetworkNodes = useSelector(
    pointElementSelectors.selectWithFilter(
      (networkNode) =>
        // only get network nodes and cut out the currently edited element if it is a point element
        networkNode.kind === 'NETWORK_NODE' && networkNode.id !== elementToUpdate?.id,
    ),
  );

  const getReadableSubtype = (subtype: PointElementSubtype | undefined) => {
    if (subtype) {
      const abbreviation = pointElementSubtypeAbbreviations.get(subtype);
      if (abbreviation) {
        return abbreviation;
      }
    }

    return toHumanReadable(subtype);
  };

  return (
    <Autocomplete
      defaultValue={allNetworkNodes.find((t) => t.id === elementToUpdate?.parentPointElementId)}
      getOptionLabel={(option) =>
        `${option.name}, ${formatAddress(option.address)} — ${getReadableSubtype(option.subtype)}`
      }
      renderInput={(params) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <TextField {...params} label="Parent Network Node" fullWidth />
      )}
      onChange={(e, value, reason) => {
        if (reason === 'selectOption')
          updateProp('parentPointElementId', (value as PointElement).id);
        else if (reason === 'clear') updateProp('parentPointElementId', '');
      }}
      options={allNetworkNodes}
      openOnFocus
      componentsProps={{ paper: { elevation: 24 } }}
      groupBy={() => ''} // this creates one group of all items
      renderGroup={({ key, children }) => (
        <Grid container key={key} fontSize={(theme) => theme.typography.body2.fontSize}>
          <ListItem
            sx={(theme) => ({
              position: 'sticky',
              top: theme.spacing(-1),
              zIndex: 1,
              backgroundColor: lighten(theme.palette.grey[100], 0.055), // TODO this is a workaround! Should be implemented in theme!
              fontWeight: 600,
              color: theme.palette.text.secondary,
            })}
            divider
          >
            <Grid size={3}>Name</Grid>
            <Grid size={5}>Address</Grid>
            <Grid size={2}>Type</Grid>
            <Grid size={2}>Date</Grid>
          </ListItem>
          {children}
        </Grid>
      )}
      renderOption={(props, option) => (
        <ListItem
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          key={option.id}
          sx={{
            gap: (theme) => theme.spacing(2),
          }}
        >
          <Grid size={3} sx={{ overflowWrap: 'break-word' }}>
            {option.name}
          </Grid>
          <Grid
            size={5}
            sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
          >
            {formatAddress(option.address)}
          </Grid>
          <Grid size={2} sx={{ overflowWrap: 'break-word' }}>
            {getReadableSubtype(option.subtype)}
          </Grid>
          <Grid size={2}>{DateTime.fromISO(option.createdAt).toLocaleString()}</Grid>
        </ListItem>
      )}
    />
  );
};

export const ChildElementSelect = ({
  currentChildSplineElements,
  elementIdToExclude,
  onChange,
}: {
  currentChildSplineElements: SplineElement[] | undefined | null;
  elementIdToExclude: string | undefined;
  onChange: (newChildSplineElements: SplineElement[]) => void;
}) => {
  const { data: containableElementsData } =
    useGetContainableSplineElementsQuery(elementIdToExclude);

  const { data: containedAttributesData } =
    useGetSplineElementContainedAttributesQuery(elementIdToExclude);

  const containableElements = containableElementsData ?? [];

  const initiallyContainedElements = containableElements.filter((el) =>
    containedAttributesData?.some((attr) => attr.childSplineElementId === el.id),
  );

  const getValue = () => {
    if (currentChildSplineElements !== undefined && currentChildSplineElements !== null) {
      return currentChildSplineElements;
    }

    if (currentChildSplineElements === undefined && initiallyContainedElements.length > 0) {
      return initiallyContainedElements;
    }

    return [];
  };

  return (
    <Autocomplete
      multiple
      value={getValue()}
      getOptionLabel={(option) => option.name}
      renderInput={(params) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <TextField {...params} label="Contained Children" fullWidth />
      )}
      onChange={(_, value) => {
        onChange(value);
      }}
      options={containableElements}
      openOnFocus
      componentsProps={{ paper: { elevation: 24 } }}
    />
  );
};
